import { datadogConfig } from "@/constants/datadog"
import nativeHelper from "@/lib/nativeHelper"
import { RumEvent } from "@datadog/browser-rum"
import axios, { AxiosError } from "axios"

/**
 * Sanitizes a tag name for use with Datadog.
 * https://docs.datadoghq.com/getting_started/tagging/#define-tags
 *
 * @param {string} value - The tag name to sanitize.
 * @returns {string|undefined} The sanitized tag name.
 */
export const sanitizeTagName = (value: string): string | undefined =>
  value?.toLowerCase().replaceAll(/[^a-z0-9_:./-]/g, "_")

export const commonDatadogParams = {
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
  site: datadogConfig.site,
  service: import.meta.env.VITE_DATADOG_SERVICE_NAME,
  sessionSampleRate: 100,
  trackSessionAcrossSubdomains: true,
}

export const isServerError = <ResponseType>(
  error: unknown,
): error is AxiosError<ResponseType> => {
  return axios.isAxiosError(error)
}

export const canInitializeDatadog = () =>
  !!__APP_VERCEL_BRANCH__ || nativeHelper.isNative()

/**
 * Deletes the host-only `_dd_s` cookie left behind by datadogLogs before it
 * shared the session across subdomains (SO-32828). The SDK reads whichever
 * `_dd_s` comes first but writes to the `.so.energy` one, so the stray copy
 * breaks session locking on Chromium. Omitting `domain` targets only the
 * host-only cookie, leaving the shared session intact.
 */
export const clearHostOnlySessionCookie = () => {
  document.cookie = "_dd_s=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/"
}

const sensitiveFieldNames: ReadonlySet<string> = new Set([
  "password",
  "checkPassword",
  "bank_account_number",
  "accountNumber",
  "sortCode",
  "sort_code",
  "token",
  "cardNumber",
  "card_number",
  "expiry",
  "code",
  "cardHolderName",
  "securityCode",
  "first_name",
  "last_name",
  "full_name",
  "accountName",
  "firstName",
  "lastName",
  "forename",
  "surname",
  "careOf",
  "phoneNumber1",
  "phoneNumber2",
])

export function redactSensitiveFields<T>(data: T): T {
  if (Array.isArray(data)) {
    return data.map((item) => redactSensitiveFields(item)) as T
  }
  if (typeof data === "object" && data !== null) {
    const obfuscatedData: Record<string, unknown> = {}
    for (const fieldName in data) {
      const fieldValue = data[fieldName]
      if (Object.hasOwn(data, fieldName)) {
        if (typeof fieldValue === "object") {
          obfuscatedData[fieldName] = redactSensitiveFields(fieldValue)
        } else if (sensitiveFieldNames.has(fieldName)) {
          obfuscatedData[fieldName] = "*REDACTED*"
        } else {
          obfuscatedData[fieldName] = fieldValue
        }
      }
    }
    return obfuscatedData as T
  }
  return data
}

export const setRequestMetadata = (event: RumEvent, error: AxiosError) => {
  const responseData = error?.response?.data
  const responseCode = error?.response?.status
  const endpointURL = error?.config?.url

  event.context = event.context || {}
  if (responseCode) event.context.responseCode = responseCode
  if (endpointURL) event.context.endpointURL = endpointURL
  if (responseData) {
    event.context.responseData = redactSensitiveFields(responseData)
  }
}
